/**
 * useLastTab.js
 *
 * Persist & restore the active bottom-tab of a navigator across app restarts /
 * OS process kills, so reopening the app lands the user on the same tab they
 * left — per app mode (Sales / Service / Logistics / Management).
 *
 * React Navigation v6 reads `initialRouteName` only ONCE on mount, so the stored
 * tab must be loaded BEFORE the <Tab.Navigator> first renders. The hook exposes a
 * `ready` gate for that: render a spinner until `ready` is true, then render the
 * navigator with the resolved `initialRouteName`. Attach `screenListeners` to the
 * navigator so every tab change is written back.
 *
 * Storage uses the durable {@link module:helpers/persistentSettings} helper
 * (AsyncStorage + SecureStore), keyed per navigator (`lastTab:<navKey>`), so
 * switching modes never clobbers another mode's remembered tab.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { getSetting, setSetting, removeSetting } from './persistentSettings';

const storageKey = (navKey) => `lastTab:${navKey}`;
const homeKey = (navKey) => `homeTab:${navKey}`;

/**
 * Read the user-pinned "Main screen" tab for a navigator (or `null` when none is
 * pinned — i.e. the "Auto (last used)" default).
 * @param {string} navKey  Unique per-navigator key, e.g. 'management'.
 * @returns {Promise<(string|null)>}
 */
export async function getHomeTab(navKey) {
    try {
        const stored = await getSetting(homeKey(navKey));

        return stored || null;
    } catch (_e) {
        return null;
    }
}

/**
 * Pin (or clear) the "Main screen" tab a navigator opens on. Passing a falsy
 * `name` removes the preference and reverts to last-used-tab memory.
 * @param {string} navKey  Unique per-navigator key, e.g. 'management'.
 * @param {?string} name   Route name to pin, or null/undefined to clear.
 * @returns {Promise<void>}
 */
export async function setHomeTab(navKey, name) {
    try {
        if (name) {
            await setSetting(homeKey(navKey), name);
        } else {
            await removeSetting(homeKey(navKey));
        }
    } catch (_e) {
        /* persistence is best-effort */
    }
}

/**
 * Pure resolution of a navigator's initial tab from the candidate values, applying
 * the priority order override → pinned "Main screen" → last-used → fallback, with
 * each candidate validated against the `valid` allow-list.
 *
 * A candidate is only rejected by `valid` when `valid` is an ARRAY: a nullish/
 * undefined `valid` means "no gating" (accept any name). Callers must therefore
 * avoid passing a still-loading (empty) array — the hook does this by deferring
 * resolution while `pending` is true — otherwise a valid pinned/stored tab would be
 * dropped for lack of a match.
 *
 * @param {object} params
 * @param {?string} [params.override] Explicit tab that wins over everything.
 * @param {?string} [params.pinned]   User-pinned "Main screen" tab (or null).
 * @param {?string} [params.stored]   Last-used tab from memory (or null).
 * @param {?string[]} [params.valid]  Allow-list; when an array, a name absent from
 *   it is rejected. Nullish = no gating.
 * @param {?string} [params.fallback] Default when nothing else applies.
 * @returns {(string|undefined)} The resolved route name (may be undefined when no
 *   fallback is supplied and nothing is valid).
 */
export function resolveInitialTab({ override, pinned, stored, valid, fallback } = {}) {
    const isValid = (name) => name && (!Array.isArray(valid) || valid.includes(name));

    if (override) {
        return override;
    }
    if (isValid(pinned)) {
        return pinned;
    }
    if (isValid(stored)) {
        return stored;
    }

    return fallback;
}

/**
 * @param {string} navKey  Unique per-navigator key, e.g. 'management', 'salesHome'.
 * @param {object} [opts]
 * @param {string} [opts.fallback]  Default tab when nothing is stored.
 * @param {string[]} [opts.valid]   Allowed tab names; a stored name absent from
 *   this list is ignored (tabs can be access/feature gated out per mode).
 * @param {string} [opts.override]  Explicit tab (deep-link / notification param)
 *   that wins over the persisted value.
 * @param {boolean} [opts.preferPinned]  When true, a user-pinned "Main screen"
 *   ({@link setHomeTab}) wins over the last-used tab. Resolution priority:
 *   override → pinned home → last tab → fallback (each validated against `valid`).
 * @param {boolean} [opts.pending]  When true the `valid` allow-list is still
 *   loading (e.g. async access/permission gating). Resolution is DEFERRED and
 *   `ready` stays false until it clears, so the navigator never mounts — and locks
 *   in `initialRouteName` — against a not-yet-ready list that would silently drop a
 *   pinned/stored tab.
 * @returns {{ready:boolean, initialRouteName:(string|undefined), screenListeners:object}}
 */
export function useLastTab(navKey, { fallback, valid, override, preferPinned = false, pending = false } = {}) {
    const [ready, setReady] = useState(false);
    const [initialRouteName, setInitialRouteName] = useState(override || fallback);
    const validRef = useRef(valid);
    validRef.current = valid;
    // Stable dependency so resolution RE-RUNS once the allow-list finally populates
    // (it can start empty while access loads) — a one-shot effect would otherwise
    // validate the pinned tab against `[]` and discard it.
    const validKey = Array.isArray(valid) ? valid.join('|') : '';

    useEffect(() => {
        let alive = true;
        // The caller's allow-list is still loading — hold the ready gate closed so the
        // navigator waits (and re-resolves) instead of committing to a stale value.
        if (pending && !override) {
            setReady(false);
            return () => { alive = false; };
        }
        (async () => {
            try {
                if (override) {
                    setInitialRouteName(override);
                    return;
                }
                // A pinned "Main screen" (if still valid) wins over last-tab memory.
                const pinned = preferPinned ? await getSetting(homeKey(navKey)) : null;
                const stored = await getSetting(storageKey(navKey));

                if (!alive) {
                    return;
                }
                // Resolve against the CURRENT allow-list — this effect re-runs (via
                // `validKey`) once it populates, so a pinned/stored tab is validated
                // against the real list and never dropped against a still-empty one.
                const resolved = resolveInitialTab({
                    pinned,
                    stored,
                    valid: validRef.current,
                    fallback,
                });

                if (resolved) {
                    setInitialRouteName(resolved);
                }
            } catch (_e) {
                /* non-fatal — fall back to default tab */
            } finally {
                if (alive) setReady(true);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [navKey, override, preferPinned, pending, validKey, fallback]);

    const screenListeners = useMemo(() => ({
        // Fires on every navigation state change inside this navigator.
        state: (e) => {
            try {
                const st = e?.data?.state;
                const name = st && Array.isArray(st.routes) ? st.routes[st.index]?.name : null;
                if (name) setSetting(storageKey(navKey), name);
            } catch (_e) { /* persistence is best-effort */ }
        },
    }), [navKey]);

    return { ready, initialRouteName, screenListeners };
}

export default useLastTab;
